// components
import { SimpleAccordion } from '@/components/molecules/SimpleAccordion/SimpleAccordion';

// router
import { Link as RouterLink } from 'react-router-dom';

// mui
import { useTheme, lighten, darken } from '@mui/material/styles';
import {
  Alert,
  Button,
  Box,
  Chip,
  Link,
  Stack,
  Typography,
} from '@mui/material';

interface BenefitCardContentProps {
  benefitName?: string;
  benefitDescription?: string;
  savingsMessage?: string;
  eligibilityInformation?: string[];
  additionalBenefits?: string[];
  applicationInstructions?: string;
  annualValueGBP?: {
    min?: number;
    max?: number;
    notes?: string;
  };
  links?: {
    info?: string;
    apply?: string;
  };
  sources?: string[];
  onContinueAssessment?: () => void;
}

const isAbsoluteURL = (url?: string) => {
  if (!url) return false;

  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * BenefitCardContent shows information about a specific benefit.
 */
export const BenefitCardContent = ({
  benefitName,
  benefitDescription,
  savingsMessage,
  eligibilityInformation,
  additionalBenefits,
  applicationInstructions,
  annualValueGBP,
  links,
  sources,
  onContinueAssessment,
}: BenefitCardContentProps) => {
  // hooks
  const theme = useTheme();

  return (
    <Stack spacing={2}>
      <Stack>
        {benefitName && (
          <Typography variant='subtitle2'>What is {benefitName}?</Typography>
        )}
        {benefitDescription && (
          <Typography variant='caption'>{benefitDescription}</Typography>
        )}
        {links?.info && (
          <Box>
            {isAbsoluteURL(links.info) ? (
              <Button
                component='a'
                variant='text'
                href={links.info}
                target='_blank'
                rel='noopener noreferrer'
              >
                Learn More
              </Button>
            ) : (
              <Button component={RouterLink} variant='text' to={links.info}>
                Learn More
              </Button>
            )}
          </Box>
        )}
      </Stack>
      {savingsMessage && (
        <Alert severity='success' title='Potential savings'>
          <Typography variant='subtitle1'>Potential savings</Typography>
          {savingsMessage}
        </Alert>
      )}
      {eligibilityInformation && (
        <Stack>
          <Typography variant='subtitle2'>Eligibility information</Typography>
          <Box
            component='ul'
            sx={{
              m: 0,
              mt: 1,
            }}
          >
            {eligibilityInformation?.map((info, index) => (
              <li key={index}>
                <Typography variant='caption'>{info}</Typography>
              </li>
            ))}
          </Box>
        </Stack>
      )}
      {additionalBenefits && (
        <Stack spacing={1}>
          <Typography variant='subtitle2'>Additional benefits</Typography>
          <Stack
            direction='row'
            sx={{
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            {additionalBenefits?.map((benefit, index) => (
              <Chip
                key={index}
                label={benefit}
                sx={{
                  backgroundColor: lighten(theme.palette.success.light, 0.9),
                  color: darken(theme.palette.success.light, 0.6),
                }}
              />
            ))}
          </Stack>
        </Stack>
      )}
      {applicationInstructions && (
        <Stack>
          <Typography variant='subtitle2'>How to apply</Typography>
          <Typography variant='caption'>{applicationInstructions}</Typography>
        </Stack>
      )}
      {sources && (
        <SimpleAccordion title='Sources'>
          {sources?.map((source, index) => (
            <Link
              key={index}
              href={source}
              target='_blank'
              rel='noopener noreferrer'
              sx={{
                color: 'info.main',
              }}
            >
              {source}
            </Link>
          ))}
        </SimpleAccordion>
      )}
      <Stack direction='row' spacing={2}>
        {links?.apply && isAbsoluteURL(links.apply) && (
          <Button
            component='a'
            variant='text'
            href={links.apply}
            target='_blank'
            rel='noopener noreferrer'
          >
            Apply Now
          </Button>
        )}
        {links?.apply && !isAbsoluteURL(links.apply) && (
          <Button component={RouterLink} variant='text' to={links.apply}>
            Apply Now
          </Button>
        )}
        {onContinueAssessment && (
          <Button variant='text' onClick={onContinueAssessment}>
            Continue Assessment
          </Button>
        )}
      </Stack>
    </Stack>
  );
};

BenefitCardContent.displayName = 'BenefitCardContent';
export default BenefitCardContent;
